import { useState, useEffect, useMemo } from "react";
import Select from "components/ui/Select";
import Input from "components/ui/Input";
import { Table, THead, TBody, TR, TH, TD, ActionButton } from "components/ui/Table";
import { SelectVendors, SelectCustomers } from "components/ui/selectors";
import {
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import {
  useCreateCvletter,
  useOneCvletter,
  useUpdateCvletter,
  useGenerateRefid,
} from "http-hooks/cvletter/useCvletter";
import { useAllVouchers } from "http-hooks/voucher/useVoucher";
import { useAllBanks } from "http-hooks/bank/useBank";
import { useNavigate, useParams } from "react-router-dom";
import { toIndianCurrencyFormat } from "utils/amount";
import useFullScreenLayout from "hooks/useFullScreenLayout";

export default function SetCvletter() {
  useFullScreenLayout();

  const navigate = useNavigate();
  const { id: encodedId } = useParams();
  // Decode the URL-encoded id
  const id = encodedId ? decodeURIComponent(encodedId) : null;
  const [selectedVendAccount, setSelectedVendAccount] = useState(null)
  const [cvletterDetail, setCvletterDetail] = useState([
    {
      srno: 1,
      customer: "",
      voucher: "",
      amount: "",
      deposite_at: "",
      rowremark: "",
      rowdate: "",
    }
  ])
  const [cvletterRecords, setCvletterRecords] = useState({
    refid: "",
    date: "",
    vendid: "",
    remark: "",
  });
  const [selectedCustAccounts, setSelectedCustAccounts] = useState(Array(cvletterDetail.length).fill(null));
  const [selectedBankAccounts, setSelectedBankAccounts] = useState(Array(cvletterDetail.length).fill(null));
  const [selectedVouchers, setSelectedVouchers] = useState(Array(cvletterDetail.length).fill(null));
  
  // Mapping to cache vouchers per vendor-customer combination
  const [voucherCache, setVoucherCache] = useState({});
  
  // State to control voucher fetching
  const [voucherFetchParams, setVoucherFetchParams] = useState({
    acdebit: "",
    accredit: "",
    exclude_cvletter: 'true',
    current_refid: '',
    shouldFetch: false,
    isEditModeFetch: false // Flag to identify edit mode bulk fetch
  });
  
  // Use the voucher hook with enabled based on shouldFetch flag
  const { data: voucherData } = useAllVouchers(
    { 
      acdebit: voucherFetchParams.acdebit, 
      accredit: voucherFetchParams.accredit,
      exclude_cvletter: voucherFetchParams.exclude_cvletter,
      current_refid: voucherFetchParams.current_refid
    },
    Boolean(voucherFetchParams.shouldFetch && voucherFetchParams.acdebit && voucherFetchParams.accredit)
  );
  
  // Update cache when voucher data is fetched
  useEffect(() => {
    if (voucherData?.data && voucherFetchParams.acdebit && voucherFetchParams.accredit) {
      if (voucherFetchParams.isEditModeFetch) {
        // Edit mode: Split and cache vouchers for each vendor-customer combination
        const accreditIds = voucherFetchParams.accredit.split(',').filter(id => id.trim());
        
        // Group vouchers by customer (accredit)
        setVoucherCache(prevCache => {
          const newCache = { ...prevCache };
          
          accreditIds.forEach(customerId => {
            const cacheKey = `${voucherFetchParams.acdebit}-${customerId.trim()}`;
            // Filter vouchers for this specific customer
            const fetchedVouchers = voucherData.data.filter(v => v.accredit === customerId.trim());
            
            // Merge with existing cache (pre-populated selected vouchers + newly fetched)
            const existingVouchers = prevCache[cacheKey] || [];
            const mergedVouchers = [...existingVouchers];
            
            // Add fetched vouchers that aren't already in cache
            fetchedVouchers.forEach(fetchedVoucher => {
              const exists = mergedVouchers.some(v => v.trid === fetchedVoucher.trid);
              if (!exists) {
                mergedVouchers.push(fetchedVoucher);
              }
            });
            
            newCache[cacheKey] = mergedVouchers;
          });
          
          return newCache;
        });
      } else {
        // Normal mode: Single vendor-customer fetch
        const cacheKey = `${voucherFetchParams.acdebit}-${voucherFetchParams.accredit}`;
        setVoucherCache(prev => ({
          ...prev,
          [cacheKey]: voucherData.data
        }));
      }
      
      // Reset shouldFetch flag after data is fetched
      setVoucherFetchParams(prev => ({
        ...prev,
        shouldFetch: false,
        isEditModeFetch: false
      }));
    }
  }, [voucherData, voucherFetchParams.acdebit, voucherFetchParams.accredit, voucherFetchParams.isEditModeFetch]);
  
  const handleCustomerAccountSlect = (index, account) => {
    const newSelectedCustAccounts = [...selectedCustAccounts];
    newSelectedCustAccounts[index] = account;
    setSelectedCustAccounts(newSelectedCustAccounts);
    
    // Reset voucher selection for this row
    const newSelectedVouchers = [...selectedVouchers];
    newSelectedVouchers[index] = null;
    setSelectedVouchers(newSelectedVouchers);
    
    // Fetch vouchers if both vendor and customer are selected
    if (selectedVendAccount && account) {
      const cacheKey = `${selectedVendAccount.ps_Id}-${account.ps_Id}`;
      
      // Only fetch if not already in cache
      if (!voucherCache[cacheKey]) {
        setVoucherFetchParams({
          acdebit: selectedVendAccount.ps_Id,
          accredit: account.ps_Id,
          exclude_cvletter: 'true',
          current_refid: id || '', // Pass current refid if editing
          shouldFetch: true,
          isEditModeFetch: false
        });
      }
    }
  };
  const handleBankAccountSelect = (index, account) => {
    const newselectedBankAccounts = [...selectedBankAccounts];
    newselectedBankAccounts[index] = account;
    setSelectedBankAccounts(newselectedBankAccounts);
  };
  const handleVoucherSelect = (index, account) => {
    const newselectedVoucher = [...selectedVouchers];
    newselectedVoucher[index] = account;
    setSelectedVouchers(newselectedVoucher);

    // Auto-populate row remark with voucher narration
    if (account?.naration) {
      const updatedDetails = [...cvletterDetail];
      updatedDetails[index] = {
        ...updatedDetails[index],
        rowremark: account.naration
      };
      setCvletterDetail(updatedDetails);
    }
    
    // Auto-add new row if this is the last row
    if (index === cvletterDetail.length - 1 && account) {
      addRowOnCvletterDetail();
    }
  };
  const handleVendorChange = (val) => {
    setSelectedVendAccount(val)
    // Clear voucher cache when vendor changes
    setVoucherCache({});
    // Reset all voucher selections
    setSelectedVouchers(Array(cvletterDetail.length).fill(null));
    // Reset fetch params
    setVoucherFetchParams({
      acdebit: "",
      accredit: "",
      exclude_cvletter: 'true',
      current_refid: '',
      shouldFetch: false,
      isEditModeFetch: false
    });
  }

  const { data: { data: allBanks } = [] } = useAllBanks();


  const { mutateAsync: createCvletter, isPending: isCvletterCreating } = useCreateCvletter();
  const { mutateAsync: updateCvletter, isPending: isCvletterUpdating } = useUpdateCvletter();
  const { data } = useOneCvletter(id);
  const { data: generatedRefidData } = useGenerateRefid();

  // Calculate total amount using useMemo
  const totalAmount = useMemo(() => {
    const sum = selectedVouchers.reduce((acc, vch) => {
      return acc + (parseFloat(vch?.tramount) || 0);
    }, 0);
    return toIndianCurrencyFormat(sum.toFixed(2), true);
  }, [selectedVouchers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCvletterRecords((prev) => {
      return {
        ...prev,
        [name]: value
      }
    })
  }
  const addRowOnCvletterDetail = () => {
    setCvletterDetail([...cvletterDetail,
    {
      srno: cvletterDetail[cvletterDetail.length - 1].srno + 1,
      customer: "",
      item: "",
      amount: "",
      deposite_at: "",
      rowremark: ""
    }
    ])
  }
  const removeItem = (itemIndex) => {
    setCvletterDetail(cvletterDetail.filter((item, itemIn) => itemIn !== itemIndex));
  };

  const handleCvDetailInputChange = (event, itemIndex) => {
    const { name, value } = event.target;
    setCvletterDetail(cvletterDetail.map((item, itemIn) => (
      itemIndex === itemIn ? { ...item, [name]: value } : item
    )));


  }
  
  // Function to get voucher options for a specific row
  const getVoucherOptionsForRow = (index) => {
    const customer = selectedCustAccounts[index];
    if (!selectedVendAccount || !customer) {
      return [];
    }
    
    const cacheKey = `${selectedVendAccount.ps_Id}-${customer.ps_Id}`;
    const vouchers = voucherCache[cacheKey] || [];
    
    return vouchers.map(voucher => ({
      value: voucher.trid,
      label: voucher.docno,
      data: voucher
    }));
  };

  // Convert banks to select options
  const bankOptions = allBanks?.map(bank => ({
    value: bank.bankname,
    label: bank.bankname,
    data: bank
  })) || [];

  // Save cvletter function
  const handleSaveCvletter = async () => {
    // Prepare details array
    const details = cvletterDetail
      .map((detail, i) => {
        if (selectedCustAccounts[i] && selectedVouchers[i]) {
          return {
            cusid: selectedCustAccounts[i].ps_Id,
            vchid: selectedVouchers[i].trid,
            vchamt: selectedVouchers[i].tramount || 0,
            rowremark: detail.rowremark || '',
            dep_bnk: selectedBankAccounts[i]?.bankname || '',
            dep_dt: detail.rowdate || ''
          };
        }
        return null;
      })
      .filter(detail => detail !== null);
    
    // Prepare the complete data object
    const completeData = {
      ...cvletterRecords,
      details
    };
    
    if (id) {
      // Update mode
      await updateCvletter({ id, data: completeData });
    } else {
      // Create mode
      await createCvletter(completeData);
    }
    navigate("/cvletter");
  };

  useEffect(() => {
    if (data && id) {
      // Populate main cvletter data
      setCvletterRecords({
        refid: data.refid || '',
        date: data.date || '',
        vendid: data.vendid || '',
        remark: data.remark || ''
      });
      
      // Populate vendor
      if (data.vendor) {
        setSelectedVendAccount(data.vendor);
      }
      
      // Populate details if they exist
      if (data.details && data.details.length > 0) {
        const detailsData = data.details.map((detail, index) => ({
          srno: index + 1,
          customer: detail.customer || null,
          voucher: detail.voucher || null,
          amount: detail.vchamt || 0,
          deposite_at: detail.voucher?.dep_bnk || '',
          rowremark: detail.rowremark || '',
          rowdate: detail.voucher?.dep_dt || ''
        }));
        
        setCvletterDetail(detailsData);
        
        // Populate selected accounts and vouchers
        setSelectedCustAccounts(data.details.map(d => d.customer || null));
        setSelectedVouchers(data.details.map(d => d.voucher || null));
        setSelectedBankAccounts(data.details.map(d => {
          if (d.voucher?.dep_bnk) {
            // Find the bank from allBanks
            const bank = allBanks?.find(b => b.bankname === d.voucher.dep_bnk);
            return bank || null;
          }
          return null;
        }));
        
        // Pre-populate cache with currently selected vouchers for edit mode
        if (data.vendor) {
          const initialCache = {};
          data.details.forEach(detail => {
            if (detail.customer && detail.voucher) {
              const cacheKey = `${data.vendor.ps_Id}-${detail.customer.ps_Id}`;
              if (!initialCache[cacheKey]) {
                initialCache[cacheKey] = [];
              }
              // Add the selected voucher to cache if not already present
              const voucherExists = initialCache[cacheKey].some(v => v.trid === detail.voucher.trid);
              if (!voucherExists) {
                initialCache[cacheKey].push(detail.voucher);
              }
            }
          });
          setVoucherCache(initialCache);
          
          // Trigger single API call to fetch ALL available vouchers for edit mode
          const uniqueCustomers = [...new Set(data.details.map(d => d.customer?.ps_Id).filter(Boolean))];
          
          if (uniqueCustomers.length > 0) {
            // Single API call with comma-separated accredit values
            // Pass current_refid to exclude vouchers from OTHER covering letters only
            setVoucherFetchParams({
              acdebit: data.vendor.ps_Id,
              accredit: uniqueCustomers.join(','),
              exclude_cvletter: 'true',
              current_refid: data.refid, // Exclude vouchers from other CVs, not this one
              shouldFetch: true,
              isEditModeFetch: true
            });
          }
        }
      }
    }
  }, [data, id, allBanks]);

  // Set generated refid for new form
  useEffect(() => {
    if (!id && generatedRefidData?.refid) {
      setCvletterRecords(prev => ({
        ...prev,
        refid: generatedRefidData.refid
      }));
    }
  }, [id, generatedRefidData]);

  useEffect(() => {
    setCvletterRecords((prev) => (
      {
        ...prev,
        vendid: selectedVendAccount?.ps_Id || null,
      }
    ))
  }, [selectedVendAccount]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            {id ? "Update" : "Add"} Covering Letter
          </h1>
        </div>
      </div>

      <form className="space-y-6">
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-base font-semibold leading-7 text-gray-900 mb-4">
            Basic Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label="Reference ID"
              name="refid"
              value={cvletterRecords.refid}
              onChange={handleInputChange}
              placeholder="Enter Reference ID"
              isRequired={true}
              maxLength={20}
              readOnly={!!id}
            />
            
            <Input
              label="Date"
              type="date"
              name="date"
              value={cvletterRecords.date}
              onChange={handleInputChange}
              isRequired={true}
              now={!id}
              withinFY={true}
            />
            
            {id ? (
              <Input
                label="Vendor"
                value={selectedVendAccount?.name || ''}
                readOnly={true}
                placeholder="Vendor"
              />
            ) : (
              <SelectVendors
                label="Vendor"
                value={selectedVendAccount}
                onChange={handleVendorChange}
                placeholder="Select Vendor"
                isRequired={true}
              />
            )}
            
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Total Amount
              </label>
              <div className="mt-2 text-lg font-semibold text-gray-900">
                {totalAmount}
              </div>
            </div>
          </div>

          <Input
            label="Remark"
            type="textarea"
            name="remark"
            value={cvletterRecords.remark}
            onChange={handleInputChange}
            placeholder="Enter remarks"
            rows={4}
          />
        </div>

        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-base font-semibold leading-7 text-gray-900 mb-4">
            Letter Details
          </h3>
          <Table>
            <THead>
              <TR>
                <TH first className="w-8 px-1.5">SNo.</TH>
                <TH>Customer</TH>
                <TH>Voucher No.</TH>
                <TH>Amount</TH>
                <TH>Deposit At</TH>
                <TH>Remark</TH>
                <TH last className="w-20 px-1.5">
                  <span className="sr-only">Action</span>
                </TH>
              </TR>
            </THead>
            <TBody>
              {cvletterDetail?.map((item, itemIndex) => (
                <TR key={item.srno}>
                  <TD first className="w-8 px-1.5">{item.srno}</TD>
                  <TD>
                    <SelectCustomers
                      value={selectedCustAccounts[itemIndex]}
                      onChange={(val) => handleCustomerAccountSlect(itemIndex, val)}
                      placeholder="Select Customer"
                      showLabel={false}
                    />
                  </TD>
                  <TD>
                    {selectedCustAccounts[itemIndex] && selectedVendAccount && (
                      <Select
                        value={selectedVouchers[itemIndex] ? {
                          value: selectedVouchers[itemIndex].trid,
                          label: selectedVouchers[itemIndex].docno
                        } : null}
                        onChange={(val) => handleVoucherSelect(itemIndex, val?.data || val)}
                        options={getVoucherOptionsForRow(itemIndex)}
                        placeholder="Select Voucher"
                        showLabel={false}
                      />
                    )}
                  </TD>
                  <TD>
                    <div className="font-medium">
                      {selectedVouchers[itemIndex]?.tramount ? toIndianCurrencyFormat(parseFloat(selectedVouchers[itemIndex].tramount).toFixed(2), true) : toIndianCurrencyFormat('0.00', true)}
                    </div>
                  </TD>
                  <TD>
                    <div className="space-y-2">
                      <Select
                        value={selectedBankAccounts[itemIndex] ? {
                          value: selectedBankAccounts[itemIndex].bankname,
                          label: selectedBankAccounts[itemIndex].bankname
                        } : null}
                        onChange={(val) => handleBankAccountSelect(itemIndex, val?.data || val)}
                        options={bankOptions}
                        placeholder="Select Bank"
                        showLabel={false}
                      />
                      <Input
                        type="date"
                        name="rowdate"
                        value={cvletterDetail[itemIndex]?.rowdate || ''}
                        onChange={(e) => handleCvDetailInputChange(e, itemIndex)}
                        showLabel={false}
                        withinFY={true}
                      />
                    </div>
                  </TD>
                  <TD>
                    <Input
                      type="textarea"
                      name="rowremark"
                      value={cvletterDetail[itemIndex]?.rowremark || ''}
                      onChange={(e) => handleCvDetailInputChange(e, itemIndex)}
                      placeholder="Remark"
                      showLabel={false}
                    />
                  </TD>
                  <TD last className="w-20 px-1.5">
                    <div className="flex flex-col gap-1">
                      <ActionButton
                        onClick={addRowOnCvletterDetail}
                        variant="success"
                        title="Add Row"
                        className="mr-0"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </ActionButton>
                      {itemIndex > 0 && (
                        <ActionButton
                          onClick={() => removeItem(itemIndex)}
                          variant="danger"
                          title="Delete Row"
                          className="mr-0"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </ActionButton>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>

        <div className="mt-6 flex items-center justify-end gap-x-6">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={() => navigate("/cvletter")}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveCvletter}
            disabled={isCvletterCreating || isCvletterUpdating}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCvletterCreating || isCvletterUpdating ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
